import React, { useState, useEffect } from 'react';
import styles from './RelatiebeheerModule.module.css';
import { Project } from '../../types';
import { ContactDetail } from './ContactDetail';
import { ContactModal } from './ContactModal';
import { Contact, ContactMoment } from './types';
import { relationshipsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface RelatiebeheerModuleProps {
  project: Project;
}

export const RelatiebeheerModule: React.FC<RelatiebeheerModuleProps> = ({ project }) => {
  const { user, isAdmin, isProjectManager } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Mock data for fallback
  const mockContacts: Contact[] = [
    {
      _id: '1',
      project: project.id,
      type: 'individual',
      name: 'Jan van der Berg',
      role: 'supporter',
      organization: {
        name: 'Gemeente Groningen',
        type: 'government'
      },
      contact: {
        email: 'j.vanderberg@groningen.nl',
        phone: '050-3671234'
      },
      influence: {
        level: 'high'
      },
      sentiment: {
        current: 'very_positive'
      },
      engagement: {
        lastContact: new Date('2024-11-28')
      },
      tags: ['Bestuurder', 'Beslisser', 'Key stakeholder'],
      notes: 'Zeer betrokken bij duurzaamheidsprojecten. Voorstander van windenergie.'
    },
    {
      _id: '2',
      project: project.id,
      type: 'organization',
      name: 'Maria Jansen',
      role: 'opponent',
      organization: {
        name: 'Bewonersvereniging Delfzijl-Noord',
        type: 'community'
      },
      contact: {
        email: 'm.jansen@bewoners-delfzijl.nl',
        phone: '0596-612345'
      },
      influence: {
        level: 'high'
      },
      sentiment: {
        current: 'negative'
      },
      engagement: {
        lastContact: new Date('2024-12-01')
      },
      tags: ['Bewonersgroep', 'Invloedrijk', 'Kritisch'],
      notes: 'Kritisch over geluidsoverlast. Wil meer transparantie over gezondheidseffecten.'
    },
    {
      _id: '3',
      project: project.id,
      type: 'organization',
      name: 'Ahmed El Amrani',
      role: 'neutral',
      organization: {
        name: 'Stichting Arbeidsmigranten Groningen',
        type: 'ngo'
      },
      contact: {
        email: 'a.elamrani@sam-groningen.nl',
        phone: '050-3456789'
      },
      influence: {
        level: 'medium'
      },
      sentiment: {
        current: 'neutral'
      },
      engagement: {
        lastContact: new Date('2024-11-15')
      },
      tags: ['Belangenbehartiger', 'Arbeidsmigranten', 'Meertalig'],
      notes: 'Kan helpen met communicatie naar arbeidsmigranten. Spreekt Arabisch, Turks en Nederlands.'
    }
  ];

  const [contactMoments, setContactMoments] = useState<ContactMoment[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState<string>('all');
  const [filterQuality, setFilterQuality] = useState<string>('all');

  // Fetch contacts from API
  useEffect(() => {
    fetchContacts();
  }, [project.id]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const data = await relationshipsAPI.getByProject(project.id);
      const relationships = data.relationships || [];

      // Map backend data to frontend Contact interface
      const mappedContacts = relationships.map((rel: any) => ({
        ...rel,
        // Keep original role but add supportLevel for UI
        supportLevel: rel.role === 'supporter' ? 'supporter' :
                     rel.role === 'opponent' ? 'opponent' : 'neutral',
        // Map influence level
        influenceLevel: rel.influence?.level || 'medium',
        // Ensure tags is an array
        tags: rel.tags || [],
        // Map lastContact from engagement
        lastContact: rel.engagement?.lastContact,
        // Map nextAction from engagement.nextContact or commitments
        nextAction: rel.engagement?.nextContact ?
          `Next contact: ${new Date(rel.engagement.nextContact).toLocaleDateString('nl-NL')}` :
          rel.commitments?.find((c: any) => !c.completed)?.description,
        // Calculate relationship quality from sentiment
        relationshipQuality: rel.sentiment?.current === 'very_positive' ? 'excellent' :
                           rel.sentiment?.current === 'positive' ? 'good' :
                           rel.sentiment?.current === 'negative' || rel.sentiment?.current === 'very_negative' ? 'needs_attention' :
                           'neutral'
      }));

      setContacts(mappedContacts);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      // Use mock data as fallback
      setContacts(mockContacts);
      setError('Using local data - API unavailable');
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (contactId: string) => {
    if (!window.confirm('Weet u zeker dat u dit contact wilt verwijderen?')) return;

    try {
      await relationshipsAPI.delete(contactId);
      setContacts(contacts.filter(c => c._id !== contactId));
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError('Failed to delete contact');
    }
  };

  const handleAddInteraction = async (contactId: string, interaction: ContactMoment) => {
    try {
      const updated = await relationshipsAPI.addInteraction(contactId, interaction);
      // Update both the contacts list and the selected contact
      setContacts(contacts.map(c => c._id === contactId ? updated : c));
      // If this is the selected contact, update it to show the new interaction immediately
      if (selectedContact && selectedContact._id === contactId) {
        setSelectedContact(updated);
      }
    } catch (err) {
      console.error('Error adding interaction:', err);
      setError('Failed to add interaction');
    }
  };

  const allTags = Array.from(new Set(contacts.flatMap(c => c.tags || [])));

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = searchTerm === '' ||
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.contact?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTag = filterTag === 'all' || (contact.tags && contact.tags.includes(filterTag));

    // Calculate relationship quality for filtering
    const relationshipQuality =
      contact.sentiment?.current === 'very_positive' ? 'excellent' :
      contact.sentiment?.current === 'positive' ? 'good' :
      contact.sentiment?.current === 'negative' || contact.sentiment?.current === 'very_negative' ? 'needs_attention' :
      'neutral';
    const matchesQuality = filterQuality === 'all' || relationshipQuality === filterQuality;

    return matchesSearch && matchesTag && matchesQuality;
  });

  const getContactMoments = (contact: Contact) => {
    return contact.interactions || [];
  };

  const relationshipColors = {
    excellent: '#4CAF50',
    good: '#277CEA',
    neutral: '#9DA6A8',
    needs_attention: '#1E3A2F'
  };

  const typeIcons = {
    meeting: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    phone: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    email: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    event: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    site_visit: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    )
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setShowCreateModal(true);
  };

  const exportToCSV = () => {
    const headers = ['Naam', 'Functie', 'Organisatie', 'Email', 'Telefoon', 'Laatste contact', 'Invloed', 'Steun'];
    const rows = contacts.map(c => [
      c.name || '',
      c.role || 'Stakeholder',
      c.organization?.name || 'Particulier',
      c.contact?.email || '',
      c.contact?.phone || '',
      c.engagement?.lastContact ? new Date(c.engagement.lastContact).toLocaleDateString('nl-NL') : '',
      c.influence?.level || 'medium',
      c.role === 'supporter' ? 'Voorstander' : c.role === 'opponent' ? 'Tegenstander' : 'Neutraal'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatiebeheer_${project.name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (selectedContact) {
    return (
      <ContactDetail
        contact={selectedContact}
        contactMoments={getContactMoments(selectedContact)}
        onClose={() => setSelectedContact(null)}
        onAddMoment={(moment) => handleAddInteraction(selectedContact._id, moment)}
        onEditContact={handleEditContact}
        onDelete={() => handleDelete(selectedContact._id)}
      />
    );
  }

  if (loading) {
    return (
      <div className={styles.relatiebeheerModule}>
        <div className={styles.loading}>Contacten laden...</div>
      </div>
    );
  }

  return (
    <div className={styles.relatiebeheerModule}>
      <div className={styles.moduleHeader}>
        <div>
          <h1>Relatiebeheer</h1>
          <p className={styles.moduleDescription}>
            Beheer contacten en relaties met stakeholders voor {project.name}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary} onClick={exportToCSV}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exporteer
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              setEditingContact(null);
              setShowCreateModal(true);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Nieuw contact
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{contacts.length}</div>
            <div className={styles.statLabel}>Totaal contacten</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="2" x2="12" y2="22" />
              <polyline points="19 5 12 12 5 5" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{contactMoments.length}</div>
            <div className={styles.statLabel}>Contactmomenten</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {contacts.filter(c => {
                const sentiment = c.sentiment?.current;
                return sentiment === 'very_positive' || sentiment === 'positive';
              }).length}
            </div>
            <div className={styles.statLabel}>Goede relaties</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {contacts.filter(c => {
                const sentiment = c.sentiment?.current;
                return sentiment === 'negative' || sentiment === 'very_negative';
              }).length}
            </div>
            <div className={styles.statLabel}>Aandacht nodig</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Zoek op naam, organisatie of functie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Alle tags</option>
            {allTags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>

          <select
            value={filterQuality}
            onChange={(e) => setFilterQuality(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Alle relaties</option>
            <option value="excellent">Uitstekend</option>
            <option value="good">Goed</option>
            <option value="neutral">Neutraal</option>
            <option value="needs_attention">Aandacht nodig</option>
          </select>
        </div>
      </div>

      {/* Contacts Grid */}
      <div className={styles.contactsGrid}>
        {filteredContacts.map(contact => {
          // Calculate relationship quality from sentiment
          const relationshipQuality =
            contact.sentiment?.current === 'very_positive' ? 'excellent' :
            contact.sentiment?.current === 'positive' ? 'good' :
            contact.sentiment?.current === 'negative' || contact.sentiment?.current === 'very_negative' ? 'needs_attention' :
            'neutral';
          const qualityColor = relationshipColors[relationshipQuality];

          return (
            <div
              key={contact._id}
              className={styles.contactCard}
              onClick={() => setSelectedContact(contact)}
            >
              <div className={styles.cardHeader}>
                <div
                  className={styles.qualityIndicator}
                  style={{ backgroundColor: qualityColor }}
                  title={contact.role || 'neutral'}
                />
                {(isAdmin || isProjectManager) && (
                  <div className={styles.cardActions}>
                    <button
                      className={styles.editBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditContact(contact);
                      }}
                      title="Bewerken"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(contact._id);
                      }}
                      title="Verwijderen"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.cardBody}>
                <h3>{contact.name}</h3>
                <p className={styles.role}>
                  {contact.role === 'supporter' ? 'VOORSTANDER' :
                   contact.role === 'opponent' ? 'TEGENSTANDER' :
                   contact.role === 'neutral' ? 'NEUTRAAL' :
                   contact.role === 'stakeholder' ? 'BELANGHEBBENDE' :
                   contact.role === 'partner' ? 'PARTNER' :
                   contact.role === 'sponsor' ? 'SPONSOR' :
                   contact.role === 'influencer' ? 'BEÏNVLOEDER' :
                   contact.role === 'decision_maker' ? 'BESLISSER' :
                   contact.role === 'advisor' ? 'ADVISEUR' :
                   contact.role || 'BELANGHEBBENDE'}
                </p>
                <p className={styles.organization}>{contact.organization?.name || 'Particulier'}</p>

                <div className={styles.cardTags}>
                  {contact.tags && contact.tags.slice(0, 2).map(tag => (
                    <span key={tag} className={styles.tag}>{tag}</span>
                  ))}
                  {contact.tags && contact.tags.length > 2 && (
                    <span className={styles.tagMore}>+{contact.tags.length - 2}</span>
                  )}
                </div>

                <div className={styles.cardMeta}>
                  <span className={`${styles.badge} ${styles[`badge${contact.influence?.level || 'medium'}`]}`}>
                    Invloed: {
                      contact.influence?.level === 'very_high' ? 'Zeer hoog' :
                      contact.influence?.level === 'high' ? 'Hoog' :
                      contact.influence?.level === 'medium' ? 'Gemiddeld' :
                      contact.influence?.level === 'low' ? 'Laag' : 'Gemiddeld'
                    }
                  </span>
                  <span className={`${styles.badge} ${styles[`badge${contact.role || 'neutral'}`]}`}>
                    {contact.role === 'supporter' ? 'Voorstander' :
                     contact.role === 'opponent' ? 'Tegenstander' : 'Neutraal'}
                  </span>
                </div>
              </div>

              <div className={styles.cardFooter}>
                {contact.engagement?.lastContact && (
                  <span className={styles.lastContact}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {new Date(contact.engagement.lastContact).toLocaleDateString('nl-NL')}
                  </span>
                )}
                {contact.engagement?.nextContact && (
                  <span className={styles.hasAction} title={`Next contact: ${new Date(contact.engagement.nextContact).toLocaleDateString('nl-NL')}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                    </svg>
                    Actie gepland
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredContacts.length === 0 && (
        <div className={styles.emptyState}>
          <p>Geen contacten gevonden</p>
          {searchTerm && (
            <button
              className={styles.btnSecondary}
              onClick={() => {
                setSearchTerm('');
                setFilterTag('all');
                setFilterQuality('all');
              }}
            >
              Filters wissen
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <ContactModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingContact(null);
          }}
          onSave={async (contactData) => {
            try {
              if (editingContact) {
                await relationshipsAPI.update(editingContact._id, contactData);
              } else {
                await relationshipsAPI.create(contactData);
              }
              await fetchContacts();
              setShowCreateModal(false);
              setEditingContact(null);
            } catch (err) {
              console.error('Error saving contact:', err);
              setError('Failed to save contact');
            }
          }}
          onDelete={editingContact ? () => handleDelete(editingContact._id) : undefined}
          contact={editingContact}
          projectId={project.id}
        />
      )}

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}
    </div>
  );
};